import { QueryClient, MutationCache } from '@tanstack/react-query'
import { logError } from '../utils/logError.js'

// The single, app-wide React Query client.
//
// Exported as a module singleton (not created inline in main.tsx) so that
// non-React code — specifically the auth store on an in-place principal switch
// — can import the SAME instance and wipe its cache. Both main.tsx (the
// <QueryClientProvider>) and authStore consume this export, guaranteeing one
// shared cache.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15 * 1000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
  mutationCache: new MutationCache({
    onError: (error: unknown) => {
      // Global fallback — logs unhandled mutation errors.
      // Components with their own try/catch on mutateAsync swallow errors before this fires.
      const e = error as { response?: { data?: { error?: string } }; message?: string }
      logError('mutation', e?.response?.data?.error || e?.message)
    },
  }),
})

/**
 * Wipe ALL cached queries + mutations.
 *
 * MUST be called on every IN-PLACE principal switch — impersonate, exit
 * impersonation, demo role switch — because React Query keys are entity-scoped,
 * not tenant-scoped: without a wipe the incoming principal would briefly read
 * the previous principal's cached, tenant-scoped data (cross-tenant bleed).
 * `logout()` is already safe — it hard-navigates, which discards the cache with
 * the JS context — so it does not need this. Finding #1, silent-state audit
 * 2026-06-07.
 */
export function resetQueryCache(): void {
  queryClient.clear()
}
